import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Button,
  FormHelperText,
  Tooltip,
  Typography
} from "@mui/material";
import { DangerousRounded, InfoOutlined } from "@mui/icons-material";
import { Player } from "video-react";
import { useDropzone } from "react-dropzone";

import {
  acceptedImagesFilesTypes,
  acceptedImagesFilesMaxSize
} from "../../utils/Constants";
import messages from "../../assets/locale/messages";
import { CloseMediaIcon, UploadIcon } from "../../utils/Icons";
import Cropper from "./Cropper";
import "./Upload.scss";

const Upload = ({
  accept,
  onChange,
  label,
  required,
  disabled,
  name,
  multiple,
  className,
  onChangeError,
  bannerClassName,
  id,
  backgroundImg,
  isInputHasErr,
  errMsg,
  onClear,
  enlarged,
  placeholderText,
  customPlaceholder,
  isVideo,
  userVideo,
  userImage,
  touchInput,
  hideErrMsg,
  showTooltip,
  showValidationMsg,
  cropAspect,
  isCropMode,
  children
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared, inputsValidations } = messages[lang];
  const [error, setError] = useState(null);

  const [videoSrc, setVideoSrc] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [cropImgSrc, setCropImgSrc] = useState(null);
  const [croppedImgOriginalData, setCroppedImgOriginalData] =
    useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [croppedImg, setCroppedImg] = useState({});

  useEffect(() => {
    if (userVideo) {
      setVideoSrc(userVideo);
    }
  }, [userVideo]);

  useEffect(() => {
    if (userImage) {
      setImgSrc(userImage);
    }
  }, [userImage]);

  useEffect(() => {
    if (document.getElementById(id || "raised-button-file")) {
      document.getElementById(id || "raised-button-file").accept =
        accept;
    }
    if (
      multiple &&
      document.getElementById(id || "raised-button-file")
    ) {
      document.getElementById(id || "raised-button-file").multiple =
        true;
    }
  }, [accept]);

  const acceptedFileType = isVideo
    ? { "video/mp4": [".mp4", ".MP4"] }
    : {
        "image/png": [".png"],
        "image/jpg": [".jpg"],
        "image/bmp": [".bmp"],
        "image/jpeg": [".jpeg"]
      };

  const validateImagesFiles = async (imagesFilesList) => {
    for (const file of imagesFilesList) {
      const isValid = await new Promise((resolve) => {
        const img = new Image();
        const objectUrl = window.URL.createObjectURL(file);
        img.src = objectUrl;
        img.onload = () => {
          window.URL.revokeObjectURL(objectUrl);
          const { naturalWidth, naturalHeight } = img;

          if (naturalWidth < 256 && naturalHeight < 256) {
            setError(inputsValidations.uploadImgSizeError);
            onChangeError &&
              onChangeError(inputsValidations.uploadImgSizeError);
            resolve(false);
          } else if (!acceptedImagesFilesTypes.includes(file.type)) {
            setError(inputsValidations.uploadTypeError);
            onChangeError &&
              onChangeError(inputsValidations.uploadTypeError);
            resolve(false);
          } else if (file.size > acceptedImagesFilesMaxSize) {
            setError(inputsValidations.uploadSizeError);
            onChangeError &&
              onChangeError(inputsValidations.uploadSizeError);
            resolve(false);
          } else {
            setError(null);
            onChangeError && onChangeError(null);
            resolve(true);
          }
        };
      });
      if (!isValid) {
        return false;
      }
    }

    return true;
  };

  const validateVideoQuality = async (VideoFile) => {
    const videoEl = document.createElement("video");

    videoEl.src = window.URL.createObjectURL(VideoFile);
    return await new Promise((resolve) => {
      videoEl.onloadedmetadata = () => {
        window.URL.revokeObjectURL(videoEl.src);

        let { type } = VideoFile;
        type = `.${type.split("/")[1]}`;

        const { videoWidth, videoHeight } = videoEl;

        if (!acceptedFileType["video/mp4"].includes(type)) {
          setError(shared.uploadVideoTypeError);
          onChangeError && onChangeError(shared.uploadVideoTypeError);
          resolve(false);
        } else if (videoWidth < 680 || videoHeight < 480) {
          setError(shared.uploadVideoSizeError);
          onChangeError && onChangeError(shared.uploadVideoSizeError);
          resolve(false);
        } else {
          setError(null);
          onChangeError && onChangeError(null);
          resolve(true);
        }
      };
    });
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles[0]) {
      if (isVideo) {
        const isFileValid = await validateVideoQuality(
          acceptedFiles[0]
        );
        if (isFileValid) {
          const url = URL.createObjectURL(acceptedFiles[0]);
          onChange(url, acceptedFiles[0]);
          setVideoSrc(url);
        }
      } else {
        if (multiple) {
          const isFileValid =
            await validateImagesFiles(acceptedFiles);
          if (isFileValid) {
            const files = acceptedFiles;
            const filesAsArray = [...files];
            const filesSrc = [];
            filesAsArray.forEach((file) => {
              filesSrc.push(URL.createObjectURL(file));
            });
            onChange(filesSrc, files);
          }
        } else {
          const isFileValid =
            await validateImagesFiles(acceptedFiles);
          if (isFileValid) {
            if (isCropMode) {
              setCropImgSrc(URL.createObjectURL(acceptedFiles[0]));
              setCropDialogOpen(true);
              setCroppedImgOriginalData(acceptedFiles[0]);
            } else {
              setImgSrc(URL.createObjectURL(acceptedFiles[0]));
              onChange(
                URL.createObjectURL(acceptedFiles[0]),
                acceptedFiles[0]
              );
            }
          }
        }
      }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileType,
    onDropRejected: (err) => {
      console.log(err);
      err.forEach((e) => {
        if (e.errors[0].code === "file-invalid-type" && isVideo) {
          setError(inputsValidations.uploadVideoTypeError);
          onChangeError &&
            onChangeError(inputsValidations.uploadVideoTypeError);
        } else if (e.errors[0].code === "file-invalid-type") {
          setError(inputsValidations.uploadImgTypeError);
          onChangeError &&
            onChangeError(inputsValidations.uploadImgTypeError);
        }
      });
    },
    maxFiles: 15,
    disabled: cropDialogOpen || videoSrc || imgSrc || disabled
  });

  return (
    <div
      {...getRootProps({
        style: { width: "100%", height: "100%" },
        onClick: (e) => e.preventDefault()
      })}>
      {videoSrc ? (
        <div className="d-flex flex-column justify-content-center align-items-center position-relative">
          <div
            className="clear-icon clear-video-icon pointer"
            onClick={() => {
              setVideoSrc(null);
              onClear();
            }}>
            <CloseMediaIcon />
          </div>
          <Player
            playsInline
            src={userVideo ? userVideo : videoSrc}
            fluid={false}
            width={"100%"}
            height={260}
          />
        </div>
      ) : !!croppedImg.croppedImgUrl || !!imgSrc ? (
        <div className="img-clear-icon-container d-flex flex-column">
          <div
            className="clear-icon clear-image-icon pointer"
            onClick={() => {
              onClear();
              setCroppedImg({});
              setImgSrc(null);
              setCropImgSrc(null);
              setCroppedImgOriginalData(null);
            }}>
            <CloseMediaIcon />
          </div>
          <div
            className={`uploaded-img circular-border-radius white-bg border ${croppedImg.croppedImgUrl && "pointer"}`}
            style={{
              backgroundImage: `url(${croppedImg.croppedImgUrl || imgSrc})`
            }}
            onClick={() => {
              if (croppedImg.croppedImgUrl) {
                setCropDialogOpen(true);
              }
            }}></div>
        </div>
      ) : (
        <div
          className={`upload-container ${className} h-100`}
          onClick={(e) => {
            if (!e.target.closest(".btn-upload")) {
              e.stopPropagation();
            }
          }}>
          <div className="upload-img-container">
            <input
              {...getInputProps({})}
              style={{ display: "none" }}
              id={id || "raised-button-file"}
              type="file"
              accept={accept}
              key={imgSrc}
              required={required}
              disabled={disabled}
              name={name}
            />
            <label
              htmlFor={id || "raised-button-file"}
              className={
                "d-flex justify-content-start position-relative grow-child-panner h-100"
              }>
              <Button
                onClick={touchInput}
                variant="contained"
                component="span"
                className={`btn-upload border-radius-8 w-100 "btn-banner
                }${enlarged ? "enlargedLogoInput" : ""}`}
                disabled={disabled}
                style={{ backgroundImage: `url(${backgroundImg})` }}>
                <div className="addIcon position-absolute d-flex flex-column align-items-center justify-content-center">
                  <div className="placeholder-container d-flex flex-column justify-content-center align-items-center ">
                    <UploadIcon className="upload-icon pb-2" />
                    <Typography
                      variant="body"
                      className="highlighted-text fw-bold fsize-14 text-center">
                      {children
                        ? children
                        : customPlaceholder
                          ? placeholderText
                          : isVideo
                            ? shared.upload.mainVideoText
                            : shared.upload.mainText}
                    </Typography>
                    {showValidationMsg && (
                      <span className="placeholder-text  fsize-14">
                        {children ? "" : shared.upload.validationText}
                      </span>
                    )}
                  </div>
                </div>
                {label}
              </Button>
            </label>
          </div>
          {(error || isInputHasErr) && (
            <div className="d-flex error-container align-items-start gap-2">
              {showTooltip && (
                <Tooltip
                  className="text-secondary mt-2"
                  title={
                    <ul className="mb-0 ps-0">
                      <li>{inputsValidations.uploadSizeError}</li>
                      <li>{inputsValidations.uploadImgSizeError}</li>
                      <li>{inputsValidations.uploadTypeError}</li>
                    </ul>
                  }>
                  <InfoOutlined />
                </Tooltip>
              )}
              {!hideErrMsg && (
                <FormHelperText
                  error={true}
                  className={`fsize-12 mt-2  ${
                    lang === "en" ? "" : "text-end "
                  } `}>
                  {error ||
                    (isInputHasErr && inputsValidations[errMsg])}
                </FormHelperText>
              )}
            </div>
          )}
        </div>
      )}
      {cropDialogOpen && (
        <Cropper
          isOpen={cropDialogOpen}
          onClose={() => {
            setCropDialogOpen(false);
          }}
          image={cropImgSrc || imgSrc}
          imageType={croppedImgOriginalData?.type}
          aspect={cropAspect}
          cropInit={croppedImg.crop}
          zoomInit={croppedImg.zoom}
          setCroppedImg={(crop, zoom, croppedImg) => {
            setCroppedImg({
              crop,
              zoom,
              croppedImgUrl: croppedImg.imgUrl
            });
            onChange({
              blob: croppedImg.blob,
              imageInfo: {
                name: croppedImgOriginalData?.name,
                type: croppedImgOriginalData?.type
              }
            });
          }}
        />
      )}
    </div>
  );
};

Upload.propTypes = {
  accept: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  onChangeError: PropTypes.func,
  bannerClassName: PropTypes.string,
  backgroundImg: PropTypes.string,
  onClear: PropTypes.func,
  id: PropTypes.string || PropTypes.number,
  isInputHasErr: PropTypes.bool,
  enlarged: PropTypes.bool,
  errMsg: PropTypes.string,
  placeholderText: PropTypes.string,
  customPlaceholder: PropTypes.bool,
  isVideo: PropTypes.bool,
  userImage: PropTypes.string,
  userVideo: PropTypes.string,
  touchInput: PropTypes.func,
  hideErrMsg: PropTypes.bool,
  showTooltip: PropTypes.bool,
  showValidationMsg: PropTypes.bool,
  cropAspect: PropTypes.number,
  isCropMode: PropTypes.bool,
  children: PropTypes.string
};

Upload.defaultProps = {
  accept: "image/*",
  label: "Upload",
  required: false,
  disabled: false
};

export default Upload;
